import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { ArrowLeft, Loader2, ChevronLeft, ChevronRight, Sparkles, Flame, ScrollText, CalendarDays, Clock, BookOpen } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const Section = ({ children, className = "" }) => (
  <motion.section variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
    className={`max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </motion.section>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="font-serif text-2xl sm:text-3xl font-bold flex items-center gap-2.5 mb-5">
    {Icon && <Icon className="h-6 w-6 text-accent" />} {children}
  </h2>
);

const paras = (text) => String(text || "").split("\n\n").filter(Boolean);

function Gallery({ images }) {
  const [i, setI] = useState(0);
  const n = images.length;
  const go = useCallback((d) => setI((p) => (p + d + n) % n), [n]);
  useEffect(() => {
    if (n <= 1) return;
    const t = setInterval(() => setI((p) => (p + 1) % n), 4500);
    return () => clearInterval(t);
  }, [n]);
  return (
    <div data-testid="blog-gallery">
      <div className="relative rounded-2xl overflow-hidden aspect-[16/9] bg-secondary border border-border">
        <AnimatePresence mode="wait">
          <motion.img key={i} src={images[i].image} alt={images[i].caption || ""}
            initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="absolute inset-0 h-full w-full object-cover" data-testid="gallery-active-image" />
        </AnimatePresence>
        {images[i].caption && (
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/75 to-transparent p-4">
            <p className="text-white text-sm">{images[i].caption}</p>
          </div>
        )}
        {n > 1 && (
          <>
            <button onClick={() => go(-1)} data-testid="gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-full bg-black/40 text-white hover:bg-black/70 backdrop-blur transition-colors"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={() => go(1)} data-testid="gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-full bg-black/40 text-white hover:bg-black/70 backdrop-blur transition-colors"><ChevronRight className="h-5 w-5" /></button>
          </>
        )}
      </div>
      {n > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          {images.map((g, idx) => (
            <button key={g.image || `thumb-${idx}`} onClick={() => setI(idx)} data-testid={`gallery-thumb-${idx}`}
              className={`h-16 w-24 shrink-0 rounded-lg overflow-hidden border-2 transition-all ${idx === i ? "border-accent opacity-100" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={g.image} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BlogDetail() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/blogs/${slug}`).then((r) => setB(r.data)).catch(() => setB(false));
    api.get(`/blogs/${slug}/related`).then((r) => setRelated(r.data || [])).catch(() => setRelated([]));
  }, [slug]);

  if (b === null) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (b === false) return <div className="min-h-[60vh] grid place-items-center"><p>Article not found. <Link to="/blog" className="text-primary underline">Back to Blog</Link></p></div>;

  const dateStr = new Date(b.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  return (
    <article className="pb-20" data-testid="blog-detail-page">
      {/* Hero */}
      <div className="relative h-[54vh] min-h-[360px] overflow-hidden">
        <motion.img src={b.image} alt="" initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.2, ease: "easeOut" }} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/25" />
        <div className="absolute inset-0 flex items-end">
          <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto w-full pb-10 text-white">
            <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-4"><ArrowLeft className="h-4 w-4" /> All Articles</Link>
            {b.topic && <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-3" data-testid="blog-topic">{b.topic}</p>}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span data-testid="blog-category-badge" className="text-xs font-semibold px-3 py-1 rounded-full bg-accent text-accent-foreground">{b.category}</span>
              {(b.type_tags || []).map((t) => (
                <span key={t} data-testid={`blog-type-tag-${t}`} className="text-xs font-medium px-3 py-1 rounded-full bg-white/15 backdrop-blur border border-white/25">{t}</span>
              ))}
            </div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }}
              className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1]">{b.title}</motion.h1>
          </div>
        </div>
      </div>

      {/* Short introduction */}
      <Section className="pt-10">
        <p className="font-serif text-xl sm:text-2xl text-foreground/90 leading-relaxed" data-testid="blog-intro">{b.intro || b.excerpt}</p>
        <div className="mt-6 h-px w-full bg-gradient-to-r from-accent/60 via-border to-transparent" />
      </Section>

      {/* Image gallery */}
      {b.gallery?.length > 0 && (
        <Section className="pt-10"><Gallery images={b.gallery} /></Section>
      )}

      {/* Mantra card */}
      {b.mantra && (
        <Section className="pt-12">
          <div data-testid="blog-mantra-card" className="rounded-2xl border border-accent/40 bg-gradient-to-br from-primary/5 to-accent/10 p-6 sm:p-8 relative overflow-hidden">
            <Sparkles className="absolute -top-3 -right-3 h-20 w-20 text-accent/10" />
            <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-4">Sacred Mantra</p>
            <p className="font-devanagari text-xl sm:text-2xl text-primary leading-relaxed mb-4" data-testid="mantra-sanskrit">{b.mantra.sanskrit}</p>
            <p className="text-base italic text-foreground/80 mb-4" data-testid="mantra-transliteration">{b.mantra.transliteration}</p>
            <div className="h-px w-16 bg-accent/50 mb-4" />
            <p className="text-sm text-muted-foreground leading-relaxed" data-testid="mantra-meaning"><span className="font-semibold text-foreground">Meaning: </span>{b.mantra.meaning}</p>
          </div>
        </Section>
      )}

      {/* Main article content */}
      {b.content && (
        <Section className="pt-12" >
          <div data-testid="blog-content" className="space-y-5">
            {paras(b.content).map((p, i) => (
              <p key={`content-${i}-${p.slice(0, 24)}`} className="text-base sm:text-lg text-foreground/85 leading-[1.9]">{p}</p>
            ))}
          </div>
        </Section>
      )}

      {/* Image + text visual storytelling */}
      {b.story && (
        <Section className="pt-12">
          <div className="grid sm:grid-cols-2 gap-6 items-center rounded-2xl border border-border bg-card overflow-hidden" data-testid="blog-story">
            <div className="h-56 sm:h-full min-h-[220px] overflow-hidden">
              <img src={b.story.image} alt="" className="h-full w-full object-cover" />
            </div>
            <div className="p-6 sm:p-8">
              {b.story.heading && <h3 className="font-serif text-xl font-semibold mb-3">{b.story.heading}</h3>}
              <p className="text-base text-muted-foreground leading-[1.9]">{b.story.text}</p>
            </div>
          </div>
        </Section>
      )}

      {/* Religious & Spiritual Significance */}
      {b.significance && (
        <Section className="pt-12">
          <SectionTitle icon={Sparkles}>Religious &amp; Spiritual Significance</SectionTitle>
          <div data-testid="blog-significance" className="space-y-5">
            {paras(b.significance).map((p, i) => (
              <p key={`sig-${i}-${p.slice(0, 24)}`} className="text-base sm:text-lg text-foreground/85 leading-[1.9]">{p}</p>
            ))}
          </div>
        </Section>
      )}

      {/* Rituals & Customs */}
      {b.rituals?.length > 0 && (
        <Section className="pt-12">
          <SectionTitle icon={Flame}>Rituals &amp; Customs</SectionTitle>
          <ul className="space-y-3" data-testid="blog-rituals">
            {b.rituals.map((r, i) => (
              <li key={`ritual-${i}-${String(r).slice(0, 32)}`} className="flex gap-3 items-start">
                <span className="mt-2 h-2 w-2 rounded-full bg-accent shrink-0" />
                <span className="text-base text-foreground/85 leading-relaxed">{r}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* How it works — step by step */}
      {b.steps?.length > 0 && (
        <Section className="pt-12">
          <SectionTitle icon={ScrollText}>How It Works — Step by Step</SectionTitle>
          <div className="space-y-4" data-testid="blog-steps">
            {b.steps.map((s, i) => (
              <motion.div key={s.title ? `step-${i}-${s.title}` : `step-${i}`} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                className="flex gap-4 rounded-2xl border border-border bg-card p-5">
                <span className="h-10 w-10 shrink-0 grid place-items-center rounded-full bg-primary text-primary-foreground font-serif font-bold">{i + 1}</span>
                <div>
                  <h4 className="font-serif text-lg font-semibold">{s.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      )}

      {/* Author / meta footer */}
      <Section className="pt-14">
        <div className="rounded-2xl border border-border bg-card p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="blog-author-meta">
          <img src={b.author_avatar || b.image} alt={b.author} className="h-14 w-14 rounded-full object-cover border-2 border-accent/40" data-testid="blog-author-avatar" />
          <div className="flex-1">
            <p className="font-serif text-lg font-semibold" data-testid="blog-author">{b.author}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
              <span className="flex items-center gap-1.5" data-testid="blog-date"><CalendarDays className="h-4 w-4 text-accent" /> {dateStr}</span>
              <span className="flex items-center gap-1.5" data-testid="blog-readtime"><Clock className="h-4 w-4 text-accent" /> {b.read_time} read</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Dynamic hashtags */}
      {b.hashtags?.length > 0 && (
        <Section className="pt-8">
          <div className="flex flex-wrap gap-2" data-testid="blog-hashtags">
            {b.hashtags.map((t) => (
              <span key={t} data-testid={`blog-hashtag-${t}`} className="text-sm font-medium px-3 py-1.5 rounded-full bg-secondary text-foreground/70 hover:text-accent hover:bg-accent/10 transition-colors cursor-default">#{t}</span>
            ))}
          </div>
        </Section>
      )}

      {/* Related reading */}
      {related.length > 0 && (
        <Section className="pt-14">
          <SectionTitle icon={BookOpen}>Related Reading</SectionTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-related">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`related-blog-${r.slug}`}
                    className="group rounded-2xl overflow-hidden border border-border bg-card hover:border-accent transition-colors flex flex-col">
                <div className="aspect-[16/10] overflow-hidden bg-secondary">
                  <img src={r.image} alt={r.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  {r.category && <p className="text-[11px] uppercase tracking-[0.2em] text-accent font-semibold mb-1.5">{r.category}</p>}
                  <p className="font-serif text-base font-semibold leading-snug line-clamp-2 group-hover:text-primary transition-colors">{r.title}</p>
                  {r.excerpt && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{r.excerpt}</p>}
                  <span className="text-xs font-semibold text-primary mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">Read article <ChevronRight className="h-3.5 w-3.5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      <Section className="pt-10">
        <Link to="/blog" data-testid="blog-back-bottom" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all"><BookOpen className="h-4 w-4" /> Explore more from the Vedic Blog</Link>
      </Section>
    </article>
  );
}
